import React from "react";
import { Link } from "react-router-dom";
import { paths } from "../../../../routes/paths";
import { money } from "../../../dashboard/dashboardHelpers";
import EmployeeAvatar from "./EmployeeAvatar";

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 10);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

// Link back to the master's own screen. Each master uses a different key, and
// they are not interchangeable: customer is composite (branchId, custId),
// employee and product are RecordGuid, vehicle is an int id.
const masterLink = (identity) => {
  switch (identity.masterType) {
    case "CUSTOMER":
      return identity.custId != null && identity.branchId != null
        ? { to: paths.customerEdit(identity.branchId, identity.custId), label: "Open customer master" }
        : null;
    case "EMPLOYEE":
      return identity.employeeRecordGuid
        ? { to: paths.employeeEdit(identity.employeeRecordGuid), label: "Open employee master" }
        : null;
    case "PRODUCT":
      return identity.productRecordGuid
        ? { to: paths.productEdit(identity.productRecordGuid), label: "Open product master" }
        : null;
    case "VEHICLE":
      return identity.vehicleId != null
        ? { to: paths.vehicleEdit(identity.vehicleId), label: "Open vehicle master" }
        : null;
    default:
      return null;
  }
};

const Info = ({ label, value }) =>
  value ? (
    <span className="badge bg-light text-secondary border fw-normal">
      {label}: {value}
    </span>
  ) : null;

const OneStopIdentity = ({ identity, photoUrl }) => {
  if (!identity) return null;
  const link = masterLink(identity);

  return (
    <div className="card mb-3 onestop-identity">
      <div className="card-body d-flex align-items-center gap-3 flex-wrap py-3">
        {identity.masterType === "EMPLOYEE" ? (
          <EmployeeAvatar name={identity.masterName} photoUrl={photoUrl} size="lg" />
        ) : (
          <span className="onestop-avatar lg">{(identity.masterKeyText || "?").slice(0, 4)}</span>
        )}

        <div className="min-w-0">
          <div className="fw-bold" style={{ fontSize: 16 }}>
            {identity.masterName}
          </div>
          <div className="text-muted" style={{ fontSize: 12.5 }}>
            {identity.subtitle}
          </div>
        </div>

        <div className="ms-auto d-flex flex-wrap gap-2 align-items-center">
          <span
            className={`badge ${
              identity.isActive ? "bg-success-subtle text-success" : "bg-secondary-subtle text-secondary"
            }`}
          >
            {identity.isActive ? "Active" : "Inactive"}
          </span>
          <Info label={identity.info1Label} value={identity.info1Value} />
          <Info label={identity.info2Label} value={identity.info2Value} />
          <Info label={identity.info3Label} value={identity.info3Value} />
          {link && (
            <Link to={link.to} className="btn btn-sm btn-outline-primary">
              {link.label}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

/** KPI strip. Every figure is derived from the same rows the tree groups, so a
 *  headline can never disagree with the detail underneath it. */
export const OneStopKpis = ({ kpis, masterType }) => {
  const fmt = (v) => (kpis.money ? money(v) : `${Number(v || 0).toLocaleString("en-KE")}`);
  const unit = kpis.money ? "" : " units";

  const tiles = [
    { k: "Transactions", v: String(kpis.count), n: `${kpis.moduleCount} module(s) touched` },
    {
      k: kpis.money ? "Money in" : "Received in",
      v: fmt(kpis.inValue),
      n: kpis.money ? "collections & returns" : `stock in${unit}`,
      tone: "in",
    },
    {
      k: kpis.money ? "Money out" : "Issued out",
      v: fmt(kpis.outValue),
      n: kpis.money ? "billed, issued & paid" : `stock out${unit}`,
      tone: "out",
    },
    {
      k: kpis.money ? "Net" : "Net movement",
      v: fmt(kpis.netValue),
      n: `${kpis.branchCount} branch(es)`,
    },
    {
      k: "Activity window",
      v: kpis.firstDate ? fmtDate(kpis.firstDate) : "—",
      n: kpis.lastDate ? `last ${fmtDate(kpis.lastDate)}` : "nothing in period",
    },
  ];

  return (
    <div className="row g-2 mb-3">
      {tiles.map((t) => (
        <div className="col-6 col-lg" key={t.k}>
          <div className="onestop-kpi">
            <div className="onestop-kpi-k">{t.k}</div>
            <div
              className={`onestop-kpi-v ${
                t.tone === "in" ? "onestop-amt-in" : t.tone === "out" ? "onestop-amt-out" : ""
              }`}
            >
              {t.v}
            </div>
            <div className="onestop-kpi-n">{t.n}</div>
          </div>
        </div>
      ))}
      {kpis.voidedCount > 0 && (
        <div className="col-12">
          <div className="text-muted" style={{ fontSize: 11.5 }}>
            {kpis.voidedCount} voided/deleted row(s) included because &ldquo;Include voided&rdquo; is on
            {masterType === "CUSTOMER" ? " - they are struck through in the detail tables." : "."}
          </div>
        </div>
      )}
    </div>
  );
};

export default OneStopIdentity;
